import { PercentageMetrics } from './types';
import { IGraphAdapter, IGraphNode } from './graphInterfaces';

/**
 * Calculate percentage metrics for COLUMN_GROUP (stage) nodes.
 * 
 * Rollup logic:
 * - COLUMN_GROUP (stage) % = average of all CAPABILITY percentages within that stage
 * 
 * Note: CAPABILITY percentages come from computeNodePercentages (metric-driven blended calculation).
 * TEAM and APPLICATION_GROUP percentages come from the API and are not calculated here.
 * 
 * @param graph - The graph containing the nodes (can be yFiles Graph or any compatible adapter)
 * @param shouldIncludeNode - Optional filter function to determine if a node should be included in calculations
 */
export function calculateGroupNodePercentages(
  graph: IGraphAdapter, 
  shouldIncludeNode?: (node: IGraphNode) => boolean
): void {
  /**
   * Recursively find all descendants of a specific type under a node
   * Optionally filters nodes based on shouldIncludeNode predicate
   */
  const findDescendantsOfType = (node: IGraphNode, targetType: string, found: IGraphNode[] = []): IGraphNode[] => {
    const children = graph.getChildren(node);
    
    for (const child of children) {
      // Skip nodes that don't pass the filter (e.g., filtered out products)
      if (shouldIncludeNode && !shouldIncludeNode(child)) {
        continue;
      }
      
      if (child.tag && child.tag.type === targetType) {
        found.push(child);
      }
      // Recurse to find descendants at any depth
      findDescendantsOfType(child, targetType, found);
    }
    
    return found;
  };

  /**
   * Calculate average percentage for all descendants of a specific type
   * Only includes descendants that are visible in the graph (respects filtering)
   */
  const getAverageForDescendantType = (node: IGraphNode, descendantType: string): PercentageMetrics | null => {
    const descendants = findDescendantsOfType(node, descendantType);
    const childMetrics: PercentageMetrics[] = [];
    
    for (const descendant of descendants) {
      if (descendant.tag && descendant.tag.percentage) {
        childMetrics.push(descendant.tag.percentage);
      }
    }

    if (childMetrics.length === 0) {
      return null;
    }

    const hasTimeseriesData = childMetrics.some(m => m.hasTimeseriesData);
    const hasTimeMetrics = childMetrics.some(m => m.hasTimeMetrics);
    const hasVolumeMetrics = childMetrics.some(m => m.hasVolumeMetrics);
    const hasQualityMetrics = childMetrics.some(m => m.hasQualityMetrics);
    const hasSentimentMetrics = childMetrics.some(m => m.hasSentimentMetrics);
    const hasCostMetrics = childMetrics.some(m => m.hasCostMetrics);
    const hasRiskMetrics = childMetrics.some(m => m.hasRiskMetrics);

    return new PercentageMetrics(
      Math.round(childMetrics.reduce((sum, m) => sum + m.TimePercentage, 0) / childMetrics.length),
      Math.round(childMetrics.reduce((sum, m) => sum + m.VolumePercentage, 0) / childMetrics.length),
      Math.round(childMetrics.reduce((sum, m) => sum + m.QualityPercentage, 0) / childMetrics.length),
      Math.round(childMetrics.reduce((sum, m) => sum + m.SentimentPercentage, 0) / childMetrics.length),
      Math.round(childMetrics.reduce((sum, m) => sum + m.CostPercentage, 0) / childMetrics.length),
      Math.round(childMetrics.reduce((sum, m) => sum + m.RiskPercentage, 0) / childMetrics.length),
      hasTimeseriesData,
      hasTimeMetrics,
      hasVolumeMetrics,
      hasQualityMetrics,
      hasSentimentMetrics,
      hasCostMetrics,
      hasRiskMetrics
    );
  };

  // Calculate Value Stream Stage percentages from Capability averages
  for (const node of graph.nodes) {
    if (node.tag && node.tag.type === 'COLUMN_GROUP') {
      const capabilityAvg = getAverageForDescendantType(node, 'CAPABILITY');
      if (capabilityAvg) {
        node.tag.percentage = capabilityAvg;
      }
    }
  }
}
